import {
  type BrokersQualifyingLifeEvent,
  type BrokersWorkflow,
  type BrokersWorkflowSummary,
} from 'utils/web_api.schemas'

import CoverageDetailsSection from './workflow_metadata/CoverageDetailsSection'
import DependentChangesSection from './workflow_metadata/DependentChangesSection'
import SourceInfoSection from './workflow_metadata/SourceInfoSection'
import WorkflowStatusMetadata from './WorkflowStatusMetadata'

type WorkflowMetadataDetailsProps = {
  workflow: BrokersWorkflowSummary
  headerVariant?: 'full' | 'compact'
}

type WorkflowMetadata = {
  start_date?: string
  dependents_added?: string[]
  dependents_removed?: string[]
  dependent_changes_detected?: boolean
  source?: string
  enrollment_event_id?: string
  qualifying_life_event_id?: string
}

// Normalize date strings for comparison by extracting just the YYYY-MM-DD portion
const normalizeDate = (dateStr: string) => dateStr.split('T')[0]

const WorkflowMetadataDetails = ({
  workflow,
  headerVariant = 'full',
}: WorkflowMetadataDetailsProps) => {
  if (!workflow.metadata) return null

  const metadata = workflow.metadata as WorkflowMetadata

  // Extract data from metadata
  const dependentsAdded = metadata.dependents_added || []
  const dependentsRemoved = metadata.dependents_removed || []

  // Qualifying Life Event from workflow (schema may not be regenerated yet)
  const qualifyingLifeEvent = (
    workflow as BrokersWorkflow & { qualifying_life_event?: BrokersQualifyingLifeEvent }
  ).qualifying_life_event

  // Check if metadata start date differs from workflow coverage start date
  const requestedStartDate =
    metadata.start_date &&
    workflow.deadline_date &&
    normalizeDate(metadata.start_date) !== normalizeDate(workflow.deadline_date)
      ? metadata.start_date
      : undefined

  // Determine which sections to show
  const hasCoverageDetails = !!workflow.deadline_date
  const hasSourceInfo =
    !!metadata.source || !!metadata.enrollment_event_id || !!qualifyingLifeEvent
  const hasDependentChanges = dependentsAdded.length > 0 || dependentsRemoved.length > 0
  const hasStatusMetadata = workflow.status && workflow.metadata

  // Don't render if nothing to display
  if (!hasCoverageDetails && !hasSourceInfo && !hasDependentChanges && !hasStatusMetadata) {
    return null
  }

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
      {/* Status metadata always comes first if present */}
      {hasStatusMetadata && <WorkflowStatusMetadata workflow={workflow as BrokersWorkflow} />}

      {/* Coverage details */}
      {hasCoverageDetails && (
        <CoverageDetailsSection
          coverageStartDate={workflow.deadline_date!}
          requestedStartDate={requestedStartDate}
          headerVariant={headerVariant}
        />
      )}

      {/* Source info */}
      {hasSourceInfo && (
        <SourceInfoSection
          source={metadata.source}
          enrollmentEventId={metadata.enrollment_event_id}
          qualifyingLifeEvent={qualifyingLifeEvent}
        />
      )}

      {/* Dependent changes */}
      {hasDependentChanges && (
        <DependentChangesSection
          dependentsAdded={dependentsAdded}
          dependentsRemoved={dependentsRemoved}
          fallbackMemberId={workflow.enrollment_info?.member_id}
          headerVariant={headerVariant}
        />
      )}
    </div>
  )
}

export default WorkflowMetadataDetails
